import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SegmentedTabs, SegmentedTabsContent } from '@/components/ui/segmented-tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useCommunities } from '@/hooks/use-communities';
import { useUser } from '@/hooks/use-user';
import { useTenant } from '@/providers/tenant-context';
import type { Community } from '@/types';
import { AccountHeader } from '@/components/layouts/AccountHeader';
import axiosInstance from '@/lib/axios';
import { postMultipartImageUpload } from '@/lib/vertex-upload';
import { toast } from 'sonner';
import {
  Loader2,
  Search,
  ArrowRight,
  ArrowLeft,
  Zap,
  Crown,
  MapPin,
  ChevronDown,
  Hash,
  Upload,
  Building2,
  Check,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AxiosError } from 'axios';
import { FormRenderer } from '@/components/onboarding/form-renderer';
import type { OnboardingFormConfig, FormAnswerValue } from '@/types';
import { buildRichOnboardingAnswers, sanitizeOnboardingAnswers } from '@/lib/onboarding-answers';
import {
  filterOnboardingFormForMembershipStatus,
  membershipStatusForJoinOnboardingPreview,
} from '@/lib/onboarding-form-for-membership';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  canCreateTopLevelCommunity,
  communitiesUserCanCreateUnder,
} from '@/lib/community-permissions';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type JoinCreateTab = 'open-explore' | 'known-explore' | 'create';

const INDIAN_CITIES: string[] = [
  'Agra',
  'Ahmedabad',
  'Ajmer',
  'Amritsar',
  'Aurangabad',
  'Bangalore',
  'Bhopal',
  'Bhubaneswar',
  'Chandigarh',
  'Chennai',
  'Coimbatore',
  'Dehradun',
  'Delhi',
  'Faridabad',
  'Ghaziabad',
  'Goa',
  'Gurgaon',
  'Guwahati',
  'Hyderabad',
  'Indore',
  'Jaipur',
  'Jalandhar',
  'Jammu',
  'Jodhpur',
  'Kanpur',
  'Kochi',
  'Kolkata',
  'Kozhikode',
  'Lucknow',
  'Ludhiana',
  'Madurai',
  'Mangalore',
  'Mumbai',
  'Mysore',
  'Nagpur',
  'Nashik',
  'Noida',
  'Patna',
  'Pune',
  'Raipur',
  'Rajkot',
  'Ranchi',
  'Surat',
  'Thiruvananthapuram',
  'Vadodara',
  'Varanasi',
  'Vijayawada',
  'Visakhapatnam',
];

interface JoinCreateViewProps {
  activeTab?: JoinCreateTab;
  defaultTab?: JoinCreateTab;
  onTabChange?: (tab: JoinCreateTab) => void;
}

function CommunityMark({ name, logo }: { name: string; logo?: string | null }) {
  return (
    <div className="h-12 w-12 rounded-xl bg-primary/15 border border-primary/20 flex items-center justify-center overflow-hidden shrink-0 shadow-sm shadow-primary/10">
      {logo ? (
        <img src={logo} alt={name} className="h-full w-full object-contain" />
      ) : (
        <Building2 className="h-5 w-5 text-primary" />
      )}
    </div>
  );
}

export function JoinCreateView({
  activeTab: controlledActiveTab,
  defaultTab = 'open-explore',
  onTabChange,
}: JoinCreateViewProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const parentFromQuery = searchParams.get('parent');
  const { user, mutate: mutateUser } = useUser();
  const { tenant, isWhitelabel } = useTenant();
  const { communities, isLoading, mutate: mutateCommunities } = useCommunities();

  const [searchTerm, setSearchTerm] = useState('');
  const [knownSearchTerm, setKnownSearchTerm] = useState('');
  const [knownSelectedCommunity, setKnownSelectedCommunity] = useState<Community | null>(null);
  const [knownCityFilter, setKnownCityFilter] = useState<string>('all');
  const [openCityFilter, setOpenCityFilter] = useState<string>('all');
  const [isCreating, setIsCreating] = useState(false);
  const [isJoining, setIsJoining] = useState<string | null>(null);
  const [uncontrolledActiveTab, setUncontrolledActiveTab] = useState<JoinCreateTab>(defaultTab);
  const activeTab = controlledActiveTab ?? uncontrolledActiveTab;

  const handleTabChange = (tab: JoinCreateTab) => {
    if (controlledActiveTab === undefined) {
      setUncontrolledActiveTab(tab);
    }
    onTabChange?.(tab);
  };

  // Onboarding State
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const [currentOnboardingCommunity, setCurrentOnboardingCommunity] = useState<Community | null>(
    null
  );
  const [onboardingConfig, setOnboardingConfig] = useState<OnboardingFormConfig | null>(null);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  // Create Form State
  const [newCommunityName, setNewCommunityName] = useState('');
  const [logo, setLogo] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [isBusinessCommunity, setIsBusinessCommunity] = useState<'yes' | 'no' | null>(null);
  const [isBusinessDirectoryEnabled, setIsBusinessDirectoryEnabled] = useState(false);
  const [isLeadEnabled, setIsLeadEnabled] = useState(true);
  const [isTestimonialEnabled, setIsTestimonialEnabled] = useState(true);
  const [isThankYouNoteEnabled, setIsThankYouNoteEnabled] = useState(true);
  const [isMeetupEnabled, setIsMeetupEnabled] = useState(true);
  const [parentCommunityId, setParentCommunityId] = useState(parentFromQuery ?? '');
  const [isParentPickerOpen, setIsParentPickerOpen] = useState(false);

  // New Form States
  const [spreadCity, setSpreadCity] = useState('');
  const [spreadState, setSpreadState] = useState('');
  const [spreadCountry, setSpreadCountry] = useState('');
  const [isCustomCountry, setIsCustomCountry] = useState(false);
  const [isCustomState, setIsCustomState] = useState(false);
  const [_isCustomCity, setIsCustomCity] = useState(false);
  const [spreadLocations, setSpreadLocations] = useState<
    Array<{ country: string; state: string; city: string }>
  >([]);

  const [countriesList, setCountriesList] = useState<string[]>([]);
  const [statesList, setStatesList] = useState<string[]>([]);
  const [citiesList, setCitiesList] = useState<string[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const res = await fetch('https://countriesnow.space/api/v0.1/countries');
        const data = await res.json();
        if (!data.error && Array.isArray(data.data)) {
          const names = data.data.map((c: { country: string }) => c.country).sort();
          setCountriesList(names);
        }
      } catch (err) {
        console.error('Failed to fetch countries', err);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  const handleCountrySelect = async (value: string) => {
    if (value === 'Other') {
      setIsCustomCountry(true);
      setSpreadCountry('');
      setIsCustomState(true);
      setSpreadState('');
      setIsCustomCity(true);
      setSpreadCity('');
      setStatesList([]);
      setCitiesList([]);
    } else {
      setIsCustomCountry(false);
      setSpreadCountry(value);
      setIsCustomState(false);
      setSpreadState('');
      setIsCustomCity(false);
      setSpreadCity('');
      setStatesList([]);
      setCitiesList([]);

      if (value) {
        setLoadingStates(true);
        try {
          const res = await fetch('https://countriesnow.space/api/v0.1/countries/states', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ country: value }),
          });
          const data = await res.json();
          if (!data.error && data.data && Array.isArray(data.data.states)) {
            const names = data.data.states.map((s: { name: string }) => s.name).sort();
            setStatesList(names);
          }
        } catch (err) {
          console.error('Failed to fetch states', err);
        } finally {
          setLoadingStates(false);
        }
      }
    }
  };

  const handleStateSelect = async (value: string) => {
    if (value === 'Other') {
      setIsCustomState(true);
      setSpreadState('');
      setIsCustomCity(true);
      setSpreadCity('');
      setCitiesList([]);
    } else {
      setIsCustomState(false);
      setSpreadState(value);
      setIsCustomCity(false);
      setSpreadCity('');
      setCitiesList([]);

      if (spreadCountry && value) {
        setLoadingCities(true);
        try {
          const res = await fetch('https://countriesnow.space/api/v0.1/countries/state/cities', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ country: spreadCountry, state: value }),
          });
          const data = await res.json();
          if (!data.error && Array.isArray(data.data)) {
            const names = data.data.sort();
            setCitiesList(names);
          }
        } catch (err) {
          console.error('Failed to fetch cities', err);
        } finally {
          setLoadingCities(false);
        }
      }
    }
  };

  const _handleCitySelect = (value: string) => {
    if (value === 'Other') {
      setIsCustomCity(true);
      setSpreadCity('');
    } else {
      setIsCustomCity(false);
      setSpreadCity(value);
    }
  };

  const addSpreadLocation = () => {
    if (!spreadCountry && !spreadState && !spreadCity) return;
    setSpreadLocations((prev) => [
      ...prev,
      { country: spreadCountry, state: spreadState, city: spreadCity },
    ]);
    setSpreadCountry('');
    setSpreadState('');
    setSpreadCity('');
    setStatesList([]);
    setCitiesList([]);
    setIsCustomCountry(false);
    setIsCustomState(false);
    setIsCustomCity(false);
  };

  const removeSpreadLocation = (index: number) => {
    setSpreadLocations((prev) => prev.filter((_, i) => i !== index));
  };

  const [communityAddress, setCommunityAddress] = useState('');
  const [customPrivacy, setCustomPrivacy] = useState<'OPEN' | 'INVITE_ONLY' | null>(null);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [searchVisibility, setSearchVisibility] = useState(true);
  const [objective, setObjective] = useState<string>('Social Community');
  const [objectiveDetail, setObjectiveDetail] = useState<string>('');
  const [objectiveOpen, setObjectiveOpen] = useState(false);
  const [communityOath, setCommunityOath] = useState('');
  const [instructionsToJoin, setInstructionsToJoin] = useState('');
  const [membershipType, setMembershipType] = useState<'FREE' | 'PAID'>('FREE');
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'community-logos');
    try {
      const { url } = await postMultipartImageUpload(formData);
      setLogo(url);
      toast.success('Logo uploaded');
    } catch {
      toast.error('Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const parentOptions = useMemo(
    () => communitiesUserCanCreateUnder(user?.communities ?? []),
    [user?.communities]
  );
  const canCreateTopLevel = canCreateTopLevelCommunity({
    isWhitelabel,
    allowCreateCommunity: tenant?.allowCreateCommunity,
  });
  const showCreate = canCreateTopLevel || parentOptions.length > 0;
  const selectedParent = parentOptions.find((community) => community.id === parentCommunityId);
  const canChangeParent = canCreateTopLevel || parentOptions.length > 1;

  useEffect(() => {
    if (parentFromQuery && parentOptions.some((community) => community.id === parentFromQuery)) {
      setParentCommunityId(parentFromQuery);
      return;
    }
    if (!canCreateTopLevel && parentOptions.length === 1) {
      setParentCommunityId(parentOptions[0].id);
    }
  }, [parentFromQuery, parentOptions, canCreateTopLevel]);

  useEffect(() => {
    if (activeTab === 'create' && !showCreate) {
      handleTabChange('known-explore');
    }
  }, [activeTab, showCreate]);

  const filteredCommunities = communities.filter(
    (c: Community) =>
      (c.customPrivacy === 'OPEN' || !c.customPrivacy) &&
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (openCityFilter === 'all' ||
        (c.spreadCity || '').toLowerCase() === openCityFilter.toLowerCase())
  );

  const knownSearchResult = (() => {
    const q = knownSearchTerm.trim();
    if (!q) return { communities: [] as Community[], hint: null as string | null };

    if (/^[a-zA-Z0-9]+$/.test(q)) {
      if (q.length < 6) {
        return { communities: [], hint: 'Enter the complete 6-character invite code' };
      }
      if (q.length === 6) {
        const match = communities.filter(
          (c: Community) =>
            c.inviteCodeEnabled !== false && (c.inviteCode || '').toLowerCase() === q.toLowerCase()
        );
        return { communities: match, hint: null };
      }
      const qL = q.toLowerCase();
      return {
        communities: communities.filter((c: Community) => c.name.toLowerCase() === qL),
        hint: null,
      };
    }

    if (q.includes('/') || q.includes('-')) {
      const lastSegment = q.split('/').filter(Boolean).pop() ?? q;
      const parts = lastSegment.split('-');
      const codePart = (parts[parts.length - 1] ?? '').replace(/[^a-zA-Z0-9]/g, '');

      if (codePart.length < 6) {
        return { communities: [], hint: 'Enter the complete 6-character invite code' };
      }

      const code = codePart.slice(0, 6);
      const match = communities.filter(
        (c: Community) =>
          c.inviteCodeEnabled !== false && (c.inviteCode || '').toLowerCase() === code.toLowerCase()
      );
      return { communities: match, hint: null };
    }

    const qL = q.toLowerCase();
    return {
      communities: communities.filter((c: Community) => c.name.toLowerCase() === qL),
      hint: null,
    };
  })();

  const knownFilteredCommunities =
    knownCityFilter === 'all'
      ? knownSearchResult.communities
      : knownSearchResult.communities.filter(
          (c: Community) => (c.spreadCity || '').toLowerCase() === knownCityFilter.toLowerCase()
        );
  const knownSearchHint = knownSearchResult.hint;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreateTopLevel && !parentCommunityId) {
      toast.error('Select a parent community');
      return;
    }
    setIsCreating(true);
    try {
      const currentInput =
        spreadCountry || spreadState || spreadCity
          ? { country: spreadCountry, state: spreadState, city: spreadCity }
          : null;
      const effectiveLocations = currentInput
        ? [...spreadLocations, currentInput]
        : spreadLocations;
      const primarySpread =
        effectiveLocations.length > 0
          ? effectiveLocations[0]
          : { country: '', state: '', city: '' };

      const calculatedSpreadType = primarySpread.city
        ? 'CITY'
        : primarySpread.state
          ? 'STATE'
          : primarySpread.country
            ? 'COUNTRY'
            : 'GLOBAL';

      const { data } = await axiosInstance.post<{
        communityId: string;
        message?: string;
      }>('/api/communities', {
        name: newCommunityName,
        parentCommunityId: parentCommunityId || undefined,
        logo: logo || undefined,
        isBusinessEnabled: isBusinessCommunity === 'yes',
        isBusinessDirectoryEnabled: isBusinessCommunity === 'yes' && isBusinessDirectoryEnabled,
        isLeadEnabled: isBusinessCommunity === 'yes' && isLeadEnabled,
        isTestimonialEnabled: isBusinessCommunity === 'yes' && isTestimonialEnabled,
        isThankYouNoteEnabled: isBusinessCommunity === 'yes' && isThankYouNoteEnabled,
        isMeetupEnabled: isBusinessCommunity === 'yes' && isMeetupEnabled,
        spreadType: calculatedSpreadType,
        spreadCity: primarySpread.city || undefined,
        spreadState: primarySpread.state || undefined,
        spreadCountry: primarySpread.country || undefined,
        customPrivacy,
        requiresApproval,
        searchVisible: searchVisibility,
        objective: objective
          ? [objectiveDetail.trim() ? `${objective} - ${objectiveDetail.trim()}` : objective]
          : [],
        communityOath: communityOath.trim() || undefined,
        instructionsToJoin: instructionsToJoin.trim() || undefined,
        membershipType,
        address: communityAddress.trim() || undefined,
      });
      const newId = data?.communityId;
      if (!newId) {
        toast.error('Community was created but the response was missing an id.');
        return;
      }
      toast.success(
        parentCommunityId
          ? 'Sub-community created successfully!'
          : 'Community created successfully!'
      );
      await Promise.all([mutateUser(), mutateCommunities()]);
      navigate(`/${newId}/dashboard`, { replace: true });
    } catch (error: unknown) {
      const message =
        error instanceof AxiosError
          ? error.response?.data?.error || error.message
          : error instanceof Error
            ? error.message
            : 'Failed to create community';
      toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  const initJoinFlow = async (community: Community) => {
    setIsJoining(community.id);
    try {
      // Fetch detailed config first to check if onboarding is enabled
      const res = await axiosInstance.get(`/api/communities/${community.id}`);
      const detailedData =
        (
          res.data as {
            community?: Community & { onboardingForm?: OnboardingFormConfig };
            onboardingForm?: OnboardingFormConfig;
            onboardingFormEnabled?: boolean;
          }
        ).community ?? (res.data as Community & { onboardingForm?: OnboardingFormConfig });

      const onboardingForm = detailedData.onboardingForm;
      const previewStatus = membershipStatusForJoinOnboardingPreview(community);
      const onboardingForPreview =
        filterOnboardingFormForMembershipStatus(onboardingForm ?? null, previewStatus) ??
        onboardingForm ??
        null;

      if (
        detailedData.onboardingFormEnabled &&
        onboardingForm?.steps?.length &&
        (onboardingForPreview?.steps?.length ?? 0) > 0
      ) {
        // Open Onboarding Modal
        setCurrentOnboardingCommunity(community);
        setOnboardingConfig(onboardingForm);
        setIsOnboardingOpen(true);
      } else {
        // Direct Join
        await performJoin(community.id);
      }
    } catch (error) {
      console.error('Failed to init join flow', error);
      toast.error('Failed to check community requirements');
    } finally {
      setIsJoining(null);
    }
  };

  const performJoin = async (communityId: string, answers?: Record<string, FormAnswerValue>) => {
    try {
      const sanitized = answers ? sanitizeOnboardingAnswers(answers) : {};
      const rich =
        Object.keys(sanitized).length > 0
          ? buildRichOnboardingAnswers(onboardingConfig ?? { steps: [] }, sanitized)
          : undefined;
      await axiosInstance.post(`/api/communities/${communityId}/join`, {
        ...(rich ? { onboardingAnswers: rich } : {}),
      });
      toast.success('Joined community successfully!');
      setIsOnboardingOpen(false);
      await Promise.all([mutateUser(), mutateCommunities()]);
    } catch (error: unknown) {
      const message =
        error instanceof AxiosError
          ? error.response?.data?.error || error.message
          : error instanceof Error
            ? error.message
            : 'Failed to join community';
      toast.error(message);
    }
  };

  const handleOnboardingSubmit = async (answers: Record<string, FormAnswerValue>) => {
    if (!currentOnboardingCommunity) return;
    setIsSubmittingForm(true);
    try {
      await performJoin(currentOnboardingCommunity.id, answers);
    } finally {
      setIsSubmittingForm(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden transition-colors duration-300">
      {/* Premium ambient light background blobs */}
      <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] max-w-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[50vw] h-[50vw] max-w-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      {user ? <AccountHeader user={user} /> : null}
      <div className="flex flex-1 items-center justify-center p-[0.6rem] md:p-6 overflow-y-auto relative">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className={cn(
            'w-full relative z-10 transition-all duration-500 ease-in-out',
            activeTab === 'create' ? 'max-w-3xl' : 'max-w-3xl'
          )}
        >
          <div className="glass-card p-4 sm:p-6 rounded-[32px] relative bg-[#E6E6FA] dark:bg-[#1a1730]">
            <button
              onClick={() =>
                navigate(
                  parentFromQuery
                    ? `/create-community?parent=${parentFromQuery}`
                    : '/create-community'
                )
              }
              className="absolute top-6 left-6 md:top-8 md:left-8 flex items-center justify-center text-black dark:text-white hover:opacity-70 transition-opacity"
              aria-label="Go back"
            >
              <ArrowLeft className="h-6 w-6 stroke-[3]" />
            </button>
            <div className="text-center pt-0 pb-2 px-8">
              <h2 className="text-4xl font-extrabold tracking-tighter mb-3 bg-clip-text text-transparent bg-gradient-to-r from-primary via-primary/80 to-primary/60">
                {activeTab === 'known-explore'
                  ? 'Search a Known Community'
                  : activeTab === 'open-explore'
                    ? 'Search an Open Community'
                    : parentCommunityId
                      ? 'Create a Sub-Community'
                      : 'Build Your Community'}
              </h2>
              <p className="text-muted-foreground text-lg font-medium max-w-md mx-auto">
                {activeTab === 'known-explore'
                  ? 'Already know your community? Search by name, invite code or invite link.'
                  : activeTab === 'open-explore'
                    ? 'Discover and join open communities that welcome new members.'
                    : parentCommunityId
                      ? 'This community will sit under the parent you choose.'
                      : 'Set up your space, define the rules, and bring the right people together.'}
              </p>
              {user ? (
                <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-xl"
                    onClick={() => navigate('/events')}
                  >
                    My networking events
                  </Button>
                  <Button type="button" className="rounded-xl" onClick={() => navigate('/events/create')}>
                    Create conclave
                  </Button>
                </div>
              ) : null}
            </div>
            <div className="pb-10">
              <SegmentedTabs
                defaultValue={defaultTab}
                value={activeTab}
                onValueChange={(val) => handleTabChange(val as JoinCreateTab)}
                className="w-full"
              >
                <SegmentedTabsContent value="open-explore" className="space-y-4">
                  {/* Search + City filter */}
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-foreground">
                      Search open communities to join.
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Search by community name…"
                        className="flex-1 h-10 rounded-xl"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <Button
                        size="icon"
                        className="h-10 w-10 rounded-xl bg-primary hover:bg-primary/90 shrink-0"
                      >
                        <Search className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 pt-0.5">
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <Label className="text-xs text-muted-foreground shrink-0">City</Label>
                      <Select
                        value={openCityFilter}
                        onValueChange={(val) => setOpenCityFilter(val)}
                      >
                        <SelectTrigger className="h-7 w-36 rounded-lg text-xs border-border/60">
                          <SelectValue placeholder="All Cities" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Cities</SelectItem>
                          {INDIAN_CITIES.map((city) => (
                            <SelectItem key={city} value={city}>
                              {city}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Results panel */}
                  <div className="rounded-xl overflow-hidden border border-border shadow-sm">
                    <div className="bg-primary px-4 py-2.5">
                      <span className="text-primary-foreground font-semibold text-sm tracking-wide">
                        Open Communities
                      </span>
                    </div>
                    <div className="divide-y divide-border max-h-[340px] overflow-y-auto bg-background">
                      {isLoading ? (
                        <div className="flex items-center justify-center gap-3 py-10">
                          <Loader2 className="h-5 w-5 animate-spin text-primary" />
                          <span className="text-muted-foreground text-sm">
                            Fetching communities…
                          </span>
                        </div>
                      ) : filteredCommunities.length > 0 ? (
                        filteredCommunities.map((community: Community) => {
                          const status = community.userStatus;
                          const requiresApproval = community.requiresApproval;
                          const subtitle =
                            status === 'JOINED'
                              ? 'Already a member'
                              : status === 'PENDING'
                                ? 'Request pending'
                                : status === 'BANNED'
                                  ? 'You are banned'
                                  : status === 'REJECTED'
                                    ? 'Request rejected'
                                    : (requiresApproval ? 'Approval required · ' : 'Open · ') +
                                      (community.isBusinessEnabled
                                        ? 'Business Hub'
                                        : 'Social Circle');

                          const actionBtn = (() => {
                            if (status === 'JOINED') {
                              return (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="rounded-lg text-xs h-7 px-3 font-semibold shrink-0"
                                  onClick={() =>
                                    (window.location.href = `/${community.id}/dashboard`)
                                  }
                                >
                                  Enter
                                </Button>
                              );
                            }
                            if (status === 'PENDING') {
                              return (
                                <span className="text-xs text-muted-foreground font-medium shrink-0">
                                  Requested
                                </span>
                              );
                            }
                            if (status === 'INVITED') {
                              return (
                                <Button
                                  size="sm"
                                  className="rounded-lg text-xs h-7 px-3 font-semibold shrink-0 bg-primary hover:bg-primary/90"
                                  onClick={() => initJoinFlow(community)}
                                >
                                  Accept
                                </Button>
                              );
                            }
                            if (status === 'BANNED' || status === 'REJECTED') {
                              return (
                                <span className="text-xs text-destructive font-medium shrink-0">
                                  {status === 'BANNED' ? 'Banned' : 'Rejected'}
                                </span>
                              );
                            }
                            return (
                              <Button
                                size="sm"
                                className="rounded-lg text-xs h-7 px-3 font-semibold shrink-0 bg-primary hover:bg-primary/90"
                                disabled={isJoining === community.id}
                                onClick={() => initJoinFlow(community)}
                              >
                                {isJoining === community.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : requiresApproval ? (
                                  'Request'
                                ) : (
                                  'Join'
                                )}
                              </Button>
                            );
                          })();

                          return (
                            <div
                              key={community.id}
                              className="flex items-center justify-between px-4 py-3 hover:bg-primary/5 transition-colors"
                            >
                              <div className="min-w-0 flex-1 mr-3">
                                <p className="font-semibold text-sm text-foreground truncate">
                                  {community.name}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
                              </div>
                              {actionBtn}
                            </div>
                          );
                        })
                      ) : (
                        <div className="flex flex-col items-center justify-center py-10 px-6 gap-2">
                          <Search className="h-7 w-7 text-muted-foreground/30" />
                          <p className="text-muted-foreground font-semibold text-sm">
                            No communities found
                          </p>
                          <p className="text-muted-foreground/60 text-xs text-center">
                            Try a different name or city filter.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </SegmentedTabsContent>

                <SegmentedTabsContent value="known-explore" className="space-y-4">
                  {/* Search + City filter */}
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold text-foreground">
                      Type keyword / Code of community / Invite Link
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Name, invite code or link…"
                        className="flex-1 h-10 rounded-xl"
                        value={knownSearchTerm}
                        onChange={(e) => {
                          setKnownSearchTerm(e.target.value);
                          setKnownSelectedCommunity(null);
                        }}
                      />
                      <Button
                        size="icon"
                        className="h-10 w-10 rounded-xl bg-primary hover:bg-primary/90 shrink-0"
                      >
                        <Search className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 pt-0.5">
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <Label className="text-xs text-muted-foreground shrink-0">City</Label>
                      <Select
                        value={knownCityFilter}
                        onValueChange={(val) => {
                          setKnownCityFilter(val);
                          setKnownSelectedCommunity(null);
                        }}
                      >
                        <SelectTrigger className="h-7 w-36 rounded-lg text-xs border-border/60">
                          <SelectValue placeholder="All Cities" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Cities</SelectItem>
                          {INDIAN_CITIES.map((city) => (
                            <SelectItem key={city} value={city}>
                              {city}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Results panel */}
                  <div className="rounded-xl overflow-hidden border border-border shadow-sm">
                    <div className="bg-primary px-4 py-2.5">
                      <span className="text-primary-foreground font-semibold text-sm tracking-wide">
                        Select Community to Join
                      </span>
                    </div>

                    <div className="divide-y divide-border max-h-[340px] overflow-y-auto bg-background">
                      {isLoading ? (
                        <div className="flex items-center justify-center gap-3 py-10">
                          <Loader2 className="h-5 w-5 animate-spin text-primary" />
                          <span className="text-muted-foreground text-sm">
                            Fetching communities…
                          </span>
                        </div>
                      ) : knownSearchTerm.trim() === '' ? (
                        <div className="flex flex-col items-center justify-center py-12 px-6 gap-2">
                          <Search className="h-8 w-8 text-muted-foreground/30" />
                          <p className="text-muted-foreground text-sm text-center">
                            Enter a community name, invite code, or invite link above.
                          </p>
                        </div>
                      ) : knownSearchHint ? (
                        <div className="flex flex-col items-center justify-center py-10 px-6 gap-2">
                          <div className="h-10 w-10 rounded-full bg-amber-400/10 flex items-center justify-center">
                            <span className="text-amber-500 font-mono font-bold">6</span>
                          </div>
                          <p className="text-amber-600 dark:text-amber-400 font-semibold text-sm text-center">
                            {knownSearchHint}
                          </p>
                          <p className="text-muted-foreground/60 text-xs text-center">
                            Invite codes are exactly 6 alphanumeric characters.
                          </p>
                        </div>
                      ) : knownFilteredCommunities.length > 0 ? (
                        knownFilteredCommunities.map((community: Community) => {
                          const isSelected = knownSelectedCommunity?.id === community.id;
                          const status = community.userStatus;
                          const privacy = community.customPrivacy || 'OPEN';
                          const subtitle =
                            status === 'JOINED'
                              ? 'Already a member'
                              : status === 'PENDING'
                                ? 'Request pending'
                                : status === 'BANNED'
                                  ? 'You are banned'
                                  : status === 'REJECTED'
                                    ? 'Request rejected'
                                    : privacy === 'CLOSED'
                                      ? 'Closed community'
                                      : privacy === 'INVITE_ONLY'
                                        ? 'Invite only · ' +
                                          (community.isBusinessEnabled
                                            ? 'Business Hub'
                                            : 'Social Circle')
                                        : (community.requiresApproval
                                            ? 'Approval required · '
                                            : 'Open · ') +
                                          (community.isBusinessEnabled
                                            ? 'Business Hub'
                                            : 'Social Circle');

                          return (
                            <button
                              key={community.id}
                              className={cn(
                                'w-full text-left px-4 py-3 transition-colors hover:bg-primary/5',
                                isSelected && 'bg-primary/10 hover:bg-primary/10'
                              )}
                              onClick={() =>
                                setKnownSelectedCommunity(isSelected ? null : community)
                              }
                            >
                              <p
                                className={cn(
                                  'font-semibold text-sm text-foreground',
                                  isSelected && 'text-primary'
                                )}
                              >
                                {community.name}
                              </p>
                              <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
                            </button>
                          );
                        })
                      ) : (
                        <div className="flex flex-col items-center justify-center py-10 px-6 gap-2">
                          <Search className="h-7 w-7 text-muted-foreground/30" />
                          <p className="text-muted-foreground font-semibold text-sm">
                            No community found
                          </p>
                          <p className="text-muted-foreground/60 text-xs text-center">
                            Check the spelling, invite code, or invite link.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </SegmentedTabsContent>

                {showCreate && (
                  <SegmentedTabsContent value="create">
                    <motion.form
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      onSubmit={handleCreate}
                      className="flex flex-col gap-2 w-full text-left"
                    >
                      <div className="p-[1.3rem] rounded-3xl border border-border/20 bg-white dark:bg-card shadow-[0_2px_16px_rgba(0,0,0,0.07)] space-y-3">
                        <div className="space-y-1.5">
                          <Label className="text-sm font-bold text-foreground">
                            Parent community
                            {!canCreateTopLevel ? <span className="text-red-500"> *</span> : null}
                          </Label>
                          {selectedParent ? (
                            <div className="flex items-center gap-3 rounded-2xl border-2 border-primary/35 bg-gradient-to-r from-primary/12 via-primary/8 to-purple-600/10 p-3 shadow-primary/15 shadow-md">
                              <CommunityMark
                                name={selectedParent.name}
                                logo={selectedParent.logo}
                              />
                              <div className="min-w-0 flex-1">
                                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">
                                  Creating under
                                </p>
                                <p className="font-extrabold text-foreground truncate font-outfit text-base">
                                  {selectedParent.name}
                                </p>
                                {selectedParent.kind === 'AGGREGATOR' ? (
                                  <p className="text-[11px] font-medium text-muted-foreground">
                                    Aggregator
                                  </p>
                                ) : null}
                              </div>
                              {canChangeParent && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="rounded-xl shrink-0 font-bold"
                                  onClick={() => setIsParentPickerOpen(true)}
                                >
                                  Change
                                </Button>
                              )}
                            </div>
                          ) : canCreateTopLevel ? (
                            <div className="flex items-center gap-3 rounded-2xl border-2 border-border/60 bg-white p-3">
                              <div className="h-12 w-12 rounded-xl bg-accent/50 border border-border/60 flex items-center justify-center shrink-0">
                                <Building2 className="h-5 w-5 text-muted-foreground" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                                  Top-level community
                                </p>
                                <p className="font-extrabold text-foreground font-outfit text-base">
                                  No parent
                                </p>
                              </div>
                              {canChangeParent && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="rounded-xl shrink-0 font-bold"
                                  onClick={() => setIsParentPickerOpen(true)}
                                >
                                  Change
                                </Button>
                              )}
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setIsParentPickerOpen(true)}
                              className="w-full flex items-center gap-3 rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 p-3 text-left hover:bg-primary/10 transition-colors"
                            >
                              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                                <Building2 className="h-5 w-5 text-primary" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-extrabold text-foreground font-outfit">
                                  Choose a parent community
                                </p>
                                <p className="text-xs text-muted-foreground font-medium">
                                  Required in this workspace
                                </p>
                              </div>
                            </button>
                          )}
                        </div>

                        <div className="border-t border-border/10 my-1" />

                        {/* Logo + Community Name side by side */}
                        <div className="flex items-center gap-3">
                          <Label htmlFor="logo-upload" className="cursor-pointer shrink-0 group">
                            <div className="flex flex-col items-center gap-1">
                              <div
                                className={cn(
                                  'relative h-16 w-16 rounded-full border-2 border-dashed transition-all overflow-hidden',
                                  logo
                                    ? 'border-primary/40 group-hover:border-primary'
                                    : 'border-border group-hover:border-primary bg-accent/40 group-hover:bg-primary/5'
                                )}
                              >
                                {logo ? (
                                  <img
                                    src={logo}
                                    alt="logo"
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <div className="h-full w-full flex flex-col items-center justify-center gap-0.5">
                                    <Upload className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                                  </div>
                                )}
                                <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  {logoUploading ? (
                                    <Loader2 className="h-5 w-5 text-white animate-spin" />
                                  ) : (
                                    <Upload className="h-5 w-5 text-white" />
                                  )}
                                </div>
                              </div>
                              <span className="text-[10px] font-semibold text-muted-foreground group-hover:text-primary transition-colors">
                                {logoUploading ? 'Uploading…' : logo ? 'Change' : 'Upload Logo'}
                              </span>
                            </div>
                            <Input
                              id="logo-upload"
                              type="file"
                              className="hidden"
                              accept="image/*"
                              onChange={handleLogoUpload}
                              disabled={logoUploading}
                            />
                          </Label>
                          <div className="flex-1 space-y-1">
                            <Label className="text-sm font-bold text-foreground">
                              Community Name
                            </Label>
                            <Input
                              id="c-name"
                              variant="glass"
                              placeholder="e.g. Innovators League"
                              className="h-10 rounded-2xl text-base px-5 transition-all"
                              value={newCommunityName}
                              onChange={(e) => setNewCommunityName(e.target.value)}
                              required
                            />
                          </div>
                        </div>

                        <div className="border-t border-border/10 my-1" />

                        {/* Community Address */}
                        <div className="space-y-1.5">
                          <div className="space-y-1">
                            <Label className="text-sm font-bold text-foreground">
                              Community Address
                            </Label>
                          </div>
                          <Input
                            variant="glass"
                            placeholder="e.g. 123 Main St, Mumbai, India"
                            value={communityAddress}
                            onChange={(e) => setCommunityAddress(e.target.value)}
                            className="h-10 rounded-xl"
                          />
                        </div>

                        <div className="border-t border-border/10 my-1" />

                        {/* 1. Spread of Community */}
                        <div className="space-y-1.5">
                          <div className="space-y-1">
                            <Label className="text-sm font-bold text-foreground">
                              Spread of the Community
                            </Label>
                          </div>

                          {spreadLocations.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {spreadLocations.map((loc, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-bold px-3 py-1.5 rounded-full border border-primary/20"
                                >
                                  <MapPin className="h-3 w-3" />
                                  {[loc.city, loc.state, loc.country].filter(Boolean).join(', ') ||
                                    'Global'}
                                  <button
                                    type="button"
                                    onClick={() => removeSpreadLocation(index)}
                                    className="hover:text-red-500 ml-1 font-extrabold text-sm leading-none"
                                  >
                                    &times;
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="space-y-3 pt-1">
                            <div className="space-y-1.5">
                              <Label className="text-xs font-bold text-muted-foreground">
                                Country
                              </Label>
                              {!isCustomCountry ? (
                                <Select value={spreadCountry} onValueChange={handleCountrySelect}>
                                  <SelectTrigger variant="glass" className="h-10 rounded-xl">
                                    <SelectValue
                                      placeholder={
                                        loadingCountries ? 'Loading countries...' : 'Select Country'
                                      }
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {countriesList.map((country) => (
                                      <SelectItem key={country} value={country}>
                                        {country}
                                      </SelectItem>
                                    ))}
                                    <SelectItem value="Other">Other...</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <div className="flex gap-2">
                                  <Input
                                    placeholder="Enter Country"
                                    value={spreadCountry}
                                    onChange={(e) => setSpreadCountry(e.target.value)}
                                    className="h-10 rounded-xl"
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => handleCountrySelect('')}
                                    className="h-10 rounded-xl text-xs px-3"
                                  >
                                    Reset
                                  </Button>
                                </div>
                              )}
                            </div>

                            <div className="space-y-1.5">
                              <Label className="text-xs font-bold text-muted-foreground">
                                State
                              </Label>
                              {!isCustomState &&
                              spreadCountry &&
                              (statesList.length > 0 || loadingStates) ? (
                                <Select value={spreadState} onValueChange={handleStateSelect}>
                                  <SelectTrigger variant="glass" className="h-10 rounded-xl">
                                    <SelectValue
                                      placeholder={
                                        loadingStates ? 'Loading states...' : 'Select State'
                                      }
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {statesList.map((state) => (
                                      <SelectItem key={state} value={state}>
                                        {state}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <div className="flex gap-2">
                                  <Input
                                    placeholder="Enter State"
                                    value={spreadState}
                                    onChange={(e) => setSpreadState(e.target.value)}
                                    disabled={!spreadCountry}
                                    className="h-10 rounded-xl"
                                  />
                                  {spreadCountry && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      onClick={() => handleStateSelect('')}
                                      className="h-10 rounded-xl text-xs px-3"
                                    >
                                      Reset
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>

                            <div className="space-y-1.5">
                              <Label className="text-xs font-bold text-muted-foreground">
                                City
                              </Label>
                              <Select
                                value={spreadCity}
                                onValueChange={(v) => setSpreadCity(v)}
                                disabled={!spreadState || loadingCities}
                              >
                                <SelectTrigger variant="glass" className="h-10 rounded-xl">
                                  <SelectValue
                                    placeholder={
                                      !spreadState
                                        ? 'Select a state first'
                                        : loadingCities
                                          ? 'Loading cities...'
                                          : 'Select City'
                                    }
                                  />
                                </SelectTrigger>
                                <SelectContent>
                                  {(citiesList.length > 0
                                    ? citiesList
                                    : spreadState
                                      ? [spreadState]
                                      : []
                                  ).map((city) => (
                                    <SelectItem key={city} value={city}>
                                      {city}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <Button
                            type="button"
                            onClick={addSpreadLocation}
                            disabled={!spreadCountry && !spreadState && !spreadCity}
                            variant="outline"
                            className="w-full h-9 rounded-xl border-dashed border-primary/30 text-primary hover:bg-primary/5 text-sm font-bold flex items-center justify-center gap-2"
                          >
                            <MapPin className="h-3.5 w-3.5" />
                            Add More Location
                          </Button>
                        </div>

                        <div className="border-t border-border/10 my-1" />

                        {/* 2. Paid or Free Membership */}
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <div className="space-y-1">
                              <Label className="text-sm font-bold text-foreground">
                                Membership Cost
                              </Label>
                              <p className="text-xs text-muted-foreground font-medium">
                                Is this community paid or free to join?
                              </p>
                            </div>
                            <div className="flex bg-accent/10 p-1 rounded-xl border border-border/40">
                              <Button
                                type="button"
                                variant={membershipType === 'FREE' ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => setMembershipType('FREE')}
                                className="rounded-lg font-bold px-3 py-1 text-xs"
                              >
                                Free
                              </Button>
                              <Button
                                type="button"
                                variant={membershipType === 'PAID' ? 'default' : 'ghost'}
                                size="sm"
                                onClick={() => setMembershipType('PAID')}
                                className="rounded-lg font-bold px-3 py-1 text-xs"
                              >
                                Paid
                              </Button>
                            </div>
                          </div>
                        </div>

                        <div className="border-t border-border/10 my-1" />

                        {/* Business Directory */}
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <Label
                              htmlFor="c-biz-directory"
                              className="text-sm font-extrabold text-foreground cursor-pointer"
                            >
                              Business Directory
                            </Label>
                            <p className="text-xs text-muted-foreground font-medium">
                              Enable member to list there business.
                            </p>
                          </div>
                          <Switch
                            id="c-biz-directory"
                            checked={isBusinessDirectoryEnabled}
                            onCheckedChange={setIsBusinessDirectoryEnabled}
                          />
                        </div>

                        <div className="border-t border-border/10 my-1" />

                        {/* 1.8. Objective / Purpose of Community */}
                        <div className="space-y-1.5">
                          <div className="space-y-1">
                            <Label className="text-sm font-bold text-foreground">
                              Objective / Purpose of Community{' '}
                              <span className="text-red-500">*</span>
                            </Label>
                            <p className="text-xs text-muted-foreground font-medium">
                              Select the primary focus area for your community.
                            </p>
                          </div>
                          {(() => {
                            const OBJECTIVES = [
                              {
                                value: 'Social Community',
                                placeholder: 'e.g. Clean the River, Save Water…',
                              },
                              {
                                value: 'Corporate Community',
                                placeholder: 'e.g. Tech Professionals, Finance Leaders…',
                              },
                              {
                                value: 'Educational Community',
                                placeholder: 'e.g. UPSC Aspirants, Engineering Students…',
                              },
                              {
                                value: 'Fun Community',
                                placeholder: 'e.g. Movie Buffs, Trekkers…',
                              },
                              {
                                value: 'Special Purpose Community',
                                placeholder: 'e.g. Cancer Awareness, Disaster Relief…',
                              },
                              { value: 'Society', placeholder: 'e.g. Residents of Shivaji Nagar…' },
                              {
                                value: 'Business Expansion',
                                placeholder: 'e.g. Startup Network, Export Traders…',
                              },
                            ];
                            return (
                              <Popover open={objectiveOpen} onOpenChange={setObjectiveOpen}>
                                <PopoverTrigger asChild>
                                  <button
                                    type="button"
                                    className="w-full h-10 px-4 rounded-xl border border-input bg-background text-sm flex items-center justify-between hover:bg-accent/40 transition-colors"
                                  >
                                    <span
                                      className={
                                        objective ? 'text-foreground' : 'text-muted-foreground'
                                      }
                                    >
                                      {objective
                                        ? objectiveDetail.trim()
                                          ? `${objective} (${objectiveDetail.trim()})`
                                          : objective
                                        : 'Select objective…'}
                                    </span>
                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="p-1 w-[var(--radix-popover-trigger-width)]"
                                  align="start"
                                  onInteractOutside={() => setObjectiveOpen(false)}
                                >
                                  {OBJECTIVES.map((obj) => (
                                    <div key={obj.value} className="mb-0.5">
                                      <button
                                        type="button"
                                        className={cn(
                                          'w-full text-left px-3 py-2.5 text-sm rounded-lg hover:bg-accent transition-colors',
                                          objective === obj.value &&
                                            'bg-accent font-semibold text-primary'
                                        )}
                                        onClick={() => {
                                          setObjective(obj.value);
                                          if (objective !== obj.value) setObjectiveDetail('');
                                        }}
                                      >
                                        {obj.value}
                                      </button>
                                      {objective === obj.value && (
                                        <div className="px-2 pt-1 pb-2">
                                          <Input
                                            placeholder={obj.placeholder}
                                            className="h-9 text-xs rounded-lg"
                                            value={objectiveDetail}
                                            onChange={(e) => setObjectiveDetail(e.target.value)}
                                            onPointerDown={(e) => e.stopPropagation()}
                                            autoFocus
                                          />
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </PopoverContent>
                              </Popover>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Business dashboard */}
                      <div className="p-[1.3rem] rounded-3xl border border-border/20 bg-white dark:bg-card shadow-[0_2px_16px_rgba(0,0,0,0.07)] space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 rounded-2xl bg-amber-500/10 text-amber-600 flex-shrink-0">
                            <Zap className="h-5 w-5" />
                          </div>
                          <div>
                            <h3 className="font-extrabold text-base text-foreground font-outfit">
                              Business Features
                            </h3>
                            <p className="text-[11px] text-muted-foreground font-medium">
                              Enable powerful tools for your community
                            </p>
                          </div>
                        </div>
                        <div className="border-t border-amber-200/40" />
                        <div className="space-y-3">
                          <p className="text-sm font-bold text-foreground">
                            Is your community a business community?
                          </p>
                          <p className="text-xs text-muted-foreground font-medium">
                            Business tools help you manage leads, testimonials, meetups &amp; more.
                          </p>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() => setIsBusinessCommunity('yes')}
                              className={cn(
                                'p-3 rounded-2xl border text-left transition-all',
                                isBusinessCommunity === 'yes'
                                  ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-400 shadow-sm'
                                  : 'bg-accent/5 border-border/40 hover:bg-accent/10'
                              )}
                            >
                              <span className="font-extrabold text-sm text-foreground block">
                                Yes, it is
                              </span>
                              <span className="text-xs font-medium text-muted-foreground">
                                Enable business tools
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setIsBusinessCommunity('no')}
                              className={cn(
                                'p-3 rounded-2xl border text-left transition-all',
                                isBusinessCommunity === 'no'
                                  ? 'bg-gray-100 dark:bg-gray-700/40 border-gray-400 dark:border-gray-500 shadow-sm'
                                  : 'bg-accent/5 border-border/40 hover:bg-accent/10'
                              )}
                            >
                              <span className="font-extrabold text-sm text-foreground block">
                                No, it isn&apos;t
                              </span>
                              <span className="text-xs font-medium text-muted-foreground">
                                Keep it social
                              </span>
                            </button>
                          </div>
                        </div>
                        {isBusinessCommunity === 'yes' && (
                          <div className="space-y-4 pt-1">
                            <div className="flex items-center gap-3 p-3 rounded-2xl bg-amber-50 dark:bg-amber-900/30 border border-amber-200/60 dark:border-amber-700/60">
                              <Crown className="h-4 w-4 text-amber-600 flex-shrink-0" />
                              <div>
                                <p className="text-xs font-extrabold text-amber-800 dark:text-amber-300">
                                  Premium Feature
                                </p>
                                <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80 font-medium">
                                  Business tools are available on the premium plan. Toggle what you
                                  need.
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="space-y-1">
                                <Label
                                  htmlFor="c-lead"
                                  className="text-sm font-extrabold text-foreground cursor-pointer"
                                >
                                  Lead Generation
                                </Label>
                                <p className="text-xs text-muted-foreground font-medium">
                                  Allow members to post and track business leads.
                                </p>
                              </div>
                              <Switch
                                id="c-lead"
                                checked={isLeadEnabled}
                                onCheckedChange={setIsLeadEnabled}
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="space-y-1">
                                <Label
                                  htmlFor="c-testimonial"
                                  className="text-sm font-extrabold text-foreground cursor-pointer"
                                >
                                  Testimonials
                                </Label>
                                <p className="text-xs text-muted-foreground font-medium">
                                  Enable members to share and view testimonials.
                                </p>
                              </div>
                              <Switch
                                id="c-testimonial"
                                checked={isTestimonialEnabled}
                                onCheckedChange={setIsTestimonialEnabled}
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="space-y-1">
                                <Label
                                  htmlFor="c-thanks"
                                  className="text-sm font-extrabold text-foreground cursor-pointer"
                                >
                                  Thank You Notes
                                </Label>
                                <p className="text-xs text-muted-foreground font-medium">
                                  Enable members to send appreciation notes.
                                </p>
                              </div>
                              <Switch
                                id="c-thanks"
                                checked={isThankYouNoteEnabled}
                                onCheckedChange={setIsThankYouNoteEnabled}
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="space-y-1">
                                <Label
                                  htmlFor="c-meetup"
                                  className="text-sm font-extrabold text-foreground cursor-pointer"
                                >
                                  Meetup Tracking
                                </Label>
                                <p className="text-xs text-muted-foreground font-medium">
                                  Enable members to record and share meetups.
                                </p>
                              </div>
                              <Switch
                                id="c-meetup"
                                checked={isMeetupEnabled}
                                onCheckedChange={setIsMeetupEnabled}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="p-[1.3rem] rounded-3xl border border-border/20 bg-white dark:bg-card shadow-[0_2px_16px_rgba(0,0,0,0.07)] space-y-3">
                        {/* 1.5. How to Join Community */}
                        <div className="space-y-2">
                          <div className="space-y-1">
                            <Label className="text-sm font-bold text-foreground">
                              Membership Type <span className="text-red-500">*</span>
                            </Label>
                            <p className="text-xs text-muted-foreground font-medium">
                              Control how members can join your community.
                            </p>
                          </div>
                          <div className="grid grid-cols-1 gap-2">
                            {/* Public (Open) */}
                            <div
                              className={cn(
                                'rounded-2xl border text-left transition-all w-full overflow-hidden',
                                customPrivacy === 'OPEN'
                                  ? 'border-primary shadow-sm'
                                  : 'border-border/40'
                              )}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  setCustomPrivacy(customPrivacy === 'OPEN' ? null : 'OPEN')
                                }
                                className={cn(
                                  'flex flex-col items-start p-3 text-left w-full transition-all',
                                  customPrivacy === 'OPEN'
                                    ? 'bg-primary/10 text-foreground'
                                    : 'bg-accent/5 text-muted-foreground hover:bg-accent/10'
                                )}
                              >
                                <span className="font-extrabold text-sm text-foreground">
                                  Public (Open)
                                </span>
                                <span className="text-xs font-medium text-muted-foreground mt-0.5">
                                  Anyone can view and join your community.
                                </span>
                              </button>
                              <AnimatePresence initial={false}>
                                {customPrivacy === 'OPEN' && (
                                  <motion.div
                                    key="open-options"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                                    className="overflow-hidden"
                                  >
                                    <div className="px-3 py-3 bg-primary/5 border-t border-primary/20 space-y-3">
                                      <div className="flex items-center justify-between">
                                        <div className="space-y-0.5">
                                          <Label className="text-sm font-bold text-foreground">
                                            Require Approval
                                          </Label>
                                          <p className="text-xs text-muted-foreground font-medium pr-[17px]">
                                            New members must be approved by an admin before
                                            accessing.
                                          </p>
                                        </div>
                                        <Switch
                                          checked={requiresApproval}
                                          onCheckedChange={setRequiresApproval}
                                        />
                                      </div>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>

                            {/* Private */}
                            <div
                              className={cn(
                                'rounded-2xl border text-left transition-all w-full overflow-hidden',
                                customPrivacy === 'INVITE_ONLY'
                                  ? 'border-primary shadow-sm'
                                  : 'border-border/40'
                              )}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  setCustomPrivacy(
                                    customPrivacy === 'INVITE_ONLY' ? null : 'INVITE_ONLY'
                                  )
                                }
                                className={cn(
                                  'flex flex-col items-start p-3 text-left w-full transition-all',
                                  customPrivacy === 'INVITE_ONLY'
                                    ? 'bg-primary/10 text-foreground'
                                    : 'bg-accent/5 text-muted-foreground hover:bg-accent/10'
                                )}
                              >
                                <span className="font-extrabold text-sm text-foreground">
                                  Private
                                </span>
                                <span className="text-xs font-medium text-muted-foreground mt-0.5">
                                  Only people with an invite link can join.
                                </span>
                              </button>
                              <AnimatePresence initial={false}>
                                {customPrivacy === 'INVITE_ONLY' && (
                                  <motion.div
                                    key="private-options"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                                    className="overflow-hidden"
                                  >
                                    <div className="px-3 py-3 bg-primary/5 border-t border-primary/20 space-y-3">
                                      <div className="flex items-center justify-between">
                                        <div className="space-y-0.5">
                                          <Label className="text-sm font-bold text-foreground">
                                            Require Approval
                                          </Label>
                                          <p className="text-xs text-muted-foreground font-medium pr-[17px]">
                                            New members must be approved by an admin before
                                            accessing.
                                          </p>
                                        </div>
                                        <Switch
                                          checked={requiresApproval}
                                          onCheckedChange={setRequiresApproval}
                                        />
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <div className="space-y-0.5">
                                          <Label className="text-sm font-bold text-foreground">
                                            Search Visibility
                                          </Label>
                                          <p className="text-xs text-muted-foreground font-medium pr-[17px]">
                                            Allow this community to appear in public search results.
                                          </p>
                                        </div>
                                        <Switch
                                          checked={searchVisibility}
                                          onCheckedChange={setSearchVisibility}
                                        />
                                      </div>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-[1.3rem] rounded-3xl border border-border/20 bg-white dark:bg-card shadow-[0_2px_16px_rgba(0,0,0,0.07)]">
                        <button
                          type="button"
                          onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                          className="w-full flex items-center justify-between text-left"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-2xl bg-emerald-500/10 text-emerald-600 flex-shrink-0">
                              <Hash className="h-5 w-5" />
                            </div>
                            <div>
                              <h3 className="font-extrabold text-base text-foreground font-outfit">
                                More
                              </h3>
                              <p className="text-[11px] text-muted-foreground font-medium">
                                Optional onboarding content
                              </p>
                            </div>
                          </div>
                          <ChevronDown
                            className={cn(
                              'h-4 w-4 text-muted-foreground transition-transform duration-200 flex-shrink-0',
                              isAdvancedOpen ? 'rotate-180' : ''
                            )}
                          />
                        </button>
                        <AnimatePresence initial={false}>
                          {isAdvancedOpen && (
                            <motion.div
                              key="more-content"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                              className="overflow-hidden"
                            >
                              <div className="space-y-5 mt-5">
                                <div className="border-t border-border/10" />
                                <div className="space-y-4">
                                  <h4 className="font-bold text-sm text-foreground uppercase tracking-wider font-outfit">
                                    Optional Settings (Onboarding)
                                  </h4>
                                  <div className="space-y-1.5">
                                    <Label
                                      htmlFor="community-oath"
                                      className="text-xs font-bold text-muted-foreground"
                                    >
                                      Community Oath
                                    </Label>
                                    <p className="text-[11px] text-muted-foreground/80 font-medium">
                                      An oath or statement that new members must accept when
                                      joining.
                                    </p>
                                    <RichTextEditor
                                      value={communityOath}
                                      onChange={(val) => setCommunityOath(val)}
                                    />
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label
                                      htmlFor="join-instructions"
                                      className="text-xs font-bold text-muted-foreground"
                                    >
                                      Instructions to Join
                                    </Label>
                                    <p className="text-[11px] text-muted-foreground/80 font-medium">
                                      Instructions shown to users when they request to join this
                                      community.
                                    </p>
                                    <RichTextEditor
                                      value={instructionsToJoin}
                                      onChange={(val) => setInstructionsToJoin(val)}
                                    />
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                      <div className="pt-2">
                        <Button
                          type="submit"
                          className="w-full h-14 rounded-2xl bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-black text-lg shadow-lg shadow-primary/25 transition-all active:scale-[0.98]"
                          disabled={isCreating}
                        >
                          {isCreating ? (
                            <Loader2 className="h-6 w-6 animate-spin" />
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              {parentCommunityId ? 'Create Sub-Community' : 'Create Your Community'}
                              <ArrowRight className="h-5 w-5" />
                            </div>
                          )}
                        </Button>
                      </div>
                    </motion.form>
                  </SegmentedTabsContent>
                )}
              </SegmentedTabs>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Onboarding Dialog */}
      <Dialog
        open={isOnboardingOpen}
        onOpenChange={(open) => {
          if (!open && !isSubmittingForm) setIsOnboardingOpen(false);
        }}
      >
        <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden bg-background sm:max-h-[85vh]">
          <DialogHeader className="p-6 pb-2 shrink-0">
            <DialogTitle className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/60">
              Welcome to {currentOnboardingCommunity?.name}
            </DialogTitle>
            <DialogDescription>
              Add a few details so the community can get to know you.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto p-6 pt-2">
            {onboardingConfig && currentOnboardingCommunity && (
              <FormRenderer
                config={
                  filterOnboardingFormForMembershipStatus(
                    onboardingConfig,
                    membershipStatusForJoinOnboardingPreview(currentOnboardingCommunity)
                  ) ?? { steps: [] }
                }
                onSubmit={handleOnboardingSubmit}
                onCancel={() => setIsOnboardingOpen(false)}
                isSubmitting={isSubmittingForm}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isParentPickerOpen} onOpenChange={setIsParentPickerOpen}>
        <DialogContent className="max-w-md p-0 gap-0 overflow-hidden bg-background">
          <DialogHeader className="p-6 pb-3">
            <DialogTitle className="text-xl font-bold font-outfit">
              Choose parent community
            </DialogTitle>
            <DialogDescription>
              The new community will sit under the one you select.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto px-4 pb-5 space-y-2">
            {canCreateTopLevel && (
              <button
                type="button"
                onClick={() => {
                  setParentCommunityId('');
                  setIsParentPickerOpen(false);
                }}
                className={cn(
                  'w-full flex items-center gap-3 rounded-2xl border p-3 text-left transition-colors',
                  !parentCommunityId
                    ? 'border-primary/40 bg-primary/10 ring-1 ring-primary/20'
                    : 'border-border/50 hover:bg-accent/40'
                )}
              >
                <div className="h-11 w-11 rounded-xl bg-accent/60 border border-border/50 flex items-center justify-center shrink-0">
                  <Building2 className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm">No parent (top-level)</p>
                  <p className="text-xs text-muted-foreground">
                    Create a community at the top of the tree
                  </p>
                </div>
                {!parentCommunityId && <Check className="h-4 w-4 text-primary shrink-0" />}
              </button>
            )}
            {parentOptions.map((community) => {
              const isSelected = community.id === parentCommunityId;
              return (
                <button
                  key={community.id}
                  type="button"
                  onClick={() => {
                    setParentCommunityId(community.id);
                    setIsParentPickerOpen(false);
                  }}
                  className={cn(
                    'w-full flex items-center gap-3 rounded-2xl border p-3 text-left transition-colors',
                    isSelected
                      ? 'border-primary/40 bg-primary/10 ring-1 ring-primary/20 shadow-sm shadow-primary/10'
                      : 'border-border/50 hover:bg-accent/40'
                  )}
                >
                  <CommunityMark name={community.name} logo={community.logo} />
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-sm truncate">{community.name}</p>
                    {community.kind === 'AGGREGATOR' ? (
                      <p className="text-xs text-muted-foreground">Aggregator</p>
                    ) : null}
                  </div>
                  {isSelected && <Check className="h-4 w-4 text-primary shrink-0" />}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
